import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CustomizableDetailTabs,
  DetailPanelGrid,
  useBackofficePageMode,
  useCustomizableDetailTabs,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { fetchBackofficeDashboard } from './api';
import {
  DashboardActionsMenu,
  DashboardDateRange,
  DashboardDateRangeSummary,
  DASHBOARD_TILE_DEFINITIONS,
} from './components';
import type { BackofficeDashboardResponse, DashboardTileId } from './types';

const PREFERENCE_SCOPE = 'dashboard:layout';
const DASHBOARD_BUILT_IN_TABS = [{ id: 'dashboard', label: 'Dashboard' }] satisfies Array<{
  id: 'dashboard';
  label: string;
}>;
const DEFAULT_TILE_IDS: DashboardTileId[] = [
  'organizations',
  'users',
  'studies',
  'active-studies',
  'interviews',
  'payg-sales',
];

function utcDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function resolveDefaultPeriod() {
  const today = new Date();
  const from = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 29)
  );
  return { from: utcDate(from), to: utcDate(today) };
}

function isValidDate(value: string | null): value is string {
  return Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));
}

function resolvePeriod(searchParams: URLSearchParams) {
  const fallback = resolveDefaultPeriod();
  const from = searchParams.get('from');
  const to = searchParams.get('to');

  if (!isValidDate(from) || !isValidDate(to) || from > to) {
    return fallback;
  }

  return { from, to };
}

export function BackofficeDashboardPage() {
  useBackofficePageTitle('Dashboard');
  const [searchParams, setSearchParams] = useSearchParams();
  const { pageMode, setPageMode } = useBackofficePageMode();
  const period = useMemo(() => resolvePeriod(searchParams), [searchParams]);
  const [data, setData] = useState<BackofficeDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const dashboardTabs = useCustomizableDetailTabs<'dashboard'>({
    preferenceScope: 'dashboard:tabs',
    builtInTabs: DASHBOARD_BUILT_IN_TABS,
    defaultTabId: 'dashboard',
    basePath: '/dashboard',
    pageMode,
  });
  const activeCustomTab = dashboardTabs.activeCustomTab;
  const preferenceScope = activeCustomTab
    ? `dashboard:layout:${activeCustomTab.id}`
    : PREFERENCE_SCOPE;
  const defaultVisibleTileIds = activeCustomTab ? [] : DEFAULT_TILE_IDS;
  const showTabs = pageMode === 'edit' || dashboardTabs.customTabs.length > 0;

  const load = useCallback(
    (signal?: AbortSignal, forceRefresh = false) => {
      setLoading(true);
      setError(null);
      return fetchBackofficeDashboard(period.from, period.to, signal, { forceRefresh })
        .then(setData)
        .catch((caught: unknown) => {
          if (!(caught instanceof DOMException && caught.name === 'AbortError')) {
            setError(caught instanceof Error ? caught.message : 'Failed to load dashboard');
          }
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    [period.from, period.to]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refreshDashboard = useCallback(() => {
    setRefreshing(true);
    void load(undefined, true).finally(() => setRefreshing(false));
  }, [load]);

  const tileItems = useMemo(
    () =>
      DASHBOARD_TILE_DEFINITIONS.map((definition) => ({
        id: definition.id,
        title: definition.title,
        description: definition.description,
        layout: {
          w: definition.defaultSize.w,
          h: definition.defaultSize.h,
          minW: 1,
          minH: 1,
          maxW: 6,
          maxH: 4,
          isResizable: true,
        },
        render: () => (data ? definition.render(data, pageMode === 'edit', definition.id) : null),
      })),
    [data, pageMode]
  );

  function changePeriod(from: string, to: string) {
    if (!isValidDate(from) || !isValidDate(to) || from > to) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set('from', from);
    next.set('to', to);
    setSearchParams(next);
  }

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        {showTabs ? (
          <div className="relative pr-12">
            <CustomizableDetailTabs tabs={dashboardTabs} />
            <div className="absolute right-0 top-0">
              <DashboardActionsMenu
                pageMode={pageMode}
                onPageModeChange={setPageMode}
                onRefresh={refreshDashboard}
                refreshing={refreshing}
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <DashboardActionsMenu
              pageMode={pageMode}
              onPageModeChange={setPageMode}
              onRefresh={refreshDashboard}
              refreshing={refreshing}
            />
          </div>
        )}

        {pageMode === 'edit' ? (
          <DashboardDateRange from={period.from} to={period.to} onChange={changePeriod} />
        ) : (
          <DashboardDateRangeSummary from={period.from} to={period.to} />
        )}

        {loading && !data ? <p className="text-body text-neutral-500">Loading dashboard…</p> : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-body text-red-700">
            {error}{' '}
            <button
              type="button"
              onClick={() => void load()}
              className="ml-2 font-semibold underline"
            >
              Retry
            </button>
          </div>
        ) : null}
        {data ? (
          <DetailPanelGrid
            resetKey={dashboardTabs.activeTabId}
            preferenceScope={preferenceScope}
            layoutMode={pageMode}
            cols={{ xxl: 12, lg: 12, md: 6, sm: 4, xs: 1, xxs: 1 }}
            defaultVisibleItemIds={defaultVisibleTileIds}
            addTileLayout={{ w: 2, h: 1 }}
            tileCatalogLabel="Add tile"
            items={tileItems}
          />
        ) : null}
      </div>
    </main>
  );
}
